import { useState, useEffect, useCallback, useRef } from "react";
import { pendingRequestsStore, deadLetterQueueStore } from "./db";

const SYNC_TAG = "dm-booking-retry";

/**
 * usePendingQueue
 *
 * Exposes the current state of the offline request queue so the UI can show
 * a badge / banner when the user has actions waiting to be synced.
 *
 * - Polls IndexedDB every `pollInterval` ms (default 30 s).
 * - Re-reads the queue whenever the browser comes back online.
 * - `drainNow()` immediately asks the SW to replay all queued items:
 *     1. Via native Background Sync (preferred, browser handles timing).
 *     2. Via postMessage TRIGGER_SYNC as a fallback for browsers that do not
 *        support the Background Sync API.
 * - After a drain attempt the queue is re-read after a short delay so the
 *   count reflects what actually succeeded.
 *
 * Returns:
 *   pendingCount       {number}   - items waiting to be synced
 *   pendingItems       {Array}    - raw IDB records
 *   deadLetterCount    {number}   - items that permanently failed
 *   deadLetterItems    {Array}    - raw dead-letter IDB records
 *   isSyncing          {boolean}  - true while a drain is in flight
 *   drainNow           {Function} - manually trigger a sync attempt
 *   dismissDeadLetter  {Function} - dismiss one dead-letter item by id
 *   dismissAllDead     {Function} - clear the entire dead-letter queue
 *   refresh            {Function} - re-read both queues on demand
 */
export function usePendingQueue(pollInterval = 30_000) {
  const [pendingItems, setPendingItems] = useState([]);
  const [deadLetterItems, setDeadLetterItems] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);
  // Mirror isSyncing in a ref so drainNow can read it synchronously without
  // having isSyncing in its dependency array (which would cause the pending
  // queue toast to re-render on every sync state change).
  const isSyncingRef = useRef(false);
  // Tracks which dead-letter item ids are currently being requeued so the
  // UI can disable their retry button and prevent double-taps.
  const [retryingIds, setRetryingIds] = useState(() => new Set());
  // Synchronous in-flight guard — a ref so the check is not subject to
  // stale-closure issues that a state-only approach would have.
  const inFlightRetries = useRef(new Set());

  const refresh = useCallback(async () => {
    try {
      const [items, dead] = await Promise.all([
        pendingRequestsStore.getAll(),
        deadLetterQueueStore.getAll(),
      ]);
      setPendingItems(items ?? []);
      setDeadLetterItems(dead ?? []);
    } catch {
      // IDB unavailable (e.g. private browsing) - silently no-op.
    }
  }, []);

  // Poll + auto-refresh when the browser comes back online.
  useEffect(() => {
    refresh();

    const interval = setInterval(refresh, pollInterval);

    const handleOnline = async () => {
      await refresh();
      // Trigger SW replay immediately so the user sees results without waiting
      // for the browser's built-in sync scheduling.
      if (!("serviceWorker" in navigator)) return;
      try {
        const reg = await navigator.serviceWorker.ready;
        if ("sync" in reg) {
          await reg.sync.register(SYNC_TAG);
        } else {
          // Fallback: tell the active SW to drain now via postMessage.
          reg.active?.postMessage({ type: "TRIGGER_SYNC", tag: SYNC_TAG });
        }
      } catch {
        // Ignore - SW may not be active yet on first load.
      }
    };

    window.addEventListener("online", handleOnline);

    return () => {
      clearInterval(interval);
      window.removeEventListener("online", handleOnline);
    };
  }, [refresh, pollInterval]);

  // Also refresh whenever SW_SYNC_COMPLETE fires so count updates instantly.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMessage = (event) => {
      if (event.data?.type === "SW_SYNC_COMPLETE") {
        // Give IDB a tick to commit deletes before reading.
        setTimeout(refresh, 500);
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () =>
      navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [refresh]);

  /**
   * Manually trigger a drain of the pending queue.
   * Safe to call even when already syncing or offline.
   */
  const drainNow = useCallback(async () => {
    if (!("serviceWorker" in navigator) || isSyncingRef.current) return;
    isSyncingRef.current = true;
    setIsSyncing(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      if ("sync" in reg) {
        await reg.sync.register(SYNC_TAG);
      } else {
        reg.active?.postMessage({ type: "TRIGGER_SYNC", tag: SYNC_TAG });
      }
    } catch {
      // Ignore.
    } finally {
      // Re-read after a short delay so the count reflects what succeeded.
      setTimeout(() => {
        refresh();
        isSyncingRef.current = false;
        setIsSyncing(false);
      }, 3_000);
    }
  }, [refresh]); // isSyncing intentionally excluded — read via ref to keep drainNow stable

  const dismissDeadLetter = useCallback(async (id) => {
    try {
      await deadLetterQueueStore.dismiss(id);
      setDeadLetterItems((prev) => prev.filter((item) => item.id !== id));
    } catch {
      // Ignore.
    }
  }, []);

  const dismissAllDead = useCallback(async () => {
    try {
      await deadLetterQueueStore.dismissAll();
      setDeadLetterItems([]);
    } catch {
      // Ignore.
    }
  }, []);

  /**
   * Move a dead-lettered item back into the pending queue so the SW will
   * attempt it again with a fresh retry counter.
   * Triggers an immediate drain attempt after re-queuing.
   * Guards against duplicate calls for the same id (double-tap / rapid click).
   */
  const retryDeadLetter = useCallback(
    async (id) => {
      // Synchronous guard: bail if this id is already being retried.
      if (inFlightRetries.current.has(id)) return;
      inFlightRetries.current.add(id);
      setRetryingIds((prev) => {
        const n = new Set(prev);
        n.add(id);
        return n;
      });

      try {
        await deadLetterQueueStore.requeue(id);
        setDeadLetterItems((prev) => prev.filter((item) => item.id !== id));
        // Trigger SW replay for the freshly re-queued item.
        if ("serviceWorker" in navigator) {
          const reg = await navigator.serviceWorker.ready;
          if ("sync" in reg) {
            await reg.sync.register(SYNC_TAG);
          } else {
            reg.active?.postMessage({ type: "TRIGGER_SYNC", tag: SYNC_TAG });
          }
        }
        // Re-read pending queue so the count badge updates immediately.
        setTimeout(refresh, 300);
      } catch {
        // Ignore — the item stays in dead-letter if requeue fails.
      } finally {
        inFlightRetries.current.delete(id);
        setRetryingIds((prev) => {
          const n = new Set(prev);
          n.delete(id);
          return n;
        });
      }
    },
    [refresh],
  );

  return {
    pendingCount: pendingItems.length,
    pendingItems,
    deadLetterCount: deadLetterItems.length,
    deadLetterItems,
    isSyncing,
    drainNow,
    dismissDeadLetter,
    dismissAllDead,
    retryDeadLetter,
    retryingIds,
    refresh,
  };
}
